import { normalizeHeader } from "@cynthai/shared";
import Papa from "papaparse";

import {
  CSV_DELIMITERS,
  sanitizeDataRow,
  sanitizeHeaderRow,
} from "@/core/lib/csv";
import { CSV_TEMPLATE_BOM } from "@/features/classification/lib/classification-csv-template";
import {
  type DraftRow,
  STATEMENT_TREE_COLUMNS,
} from "@/features/classification/lib/statement-tree";

const ROW_ID_COLUMN = "RowId";
const SPREADSHEET_FORMULA_PREFIX = /^[=+\-@＝＋－＠]/u;
const REQUIRED_COLUMNS = [
  STATEMENT_TREE_COLUMNS.statementType,
  STATEMENT_TREE_COLUMNS.sign,
  STATEMENT_TREE_COLUMNS.level1,
  STATEMENT_TREE_COLUMNS.level2,
] as const;

export type StatementTreeCsvResult =
  | { status: "ok"; rows: DraftRow[] }
  | { status: "empty" }
  | { status: "malformed" }
  | { status: "missing_columns"; columns: string[] }
  | { status: "invalid_rows"; lines: number[] };

function findColumn(headers: string[], column: string): number {
  const normalizedColumn = normalizeHeader(column);
  return headers.findIndex(
    (header) => normalizeHeader(header) === normalizedColumn,
  );
}

function valueAt(cells: (string | null)[], index: number): string | null {
  return index === -1 ? null : (cells[index] ?? null);
}

/** Parse a statement-tree CSV without applying a partial or malformed import. */
export function parseStatementTreeCsv(csv: string): StatementTreeCsvResult {
  if (!csv.trim()) return { status: "empty" };

  const parsed = Papa.parse<string[]>(csv, {
    delimiter: "",
    delimitersToGuess: CSV_DELIMITERS,
    header: false,
    skipEmptyLines: "greedy",
    dynamicTyping: false,
  });
  if (parsed.errors.some((error) => error.code !== "UndetectableDelimiter")) {
    return { status: "malformed" };
  }
  if (parsed.data.length === 0) return { status: "empty" };

  const headers = sanitizeHeaderRow(parsed.data[0]);
  const columnIndexes = new Map(
    [
      ...REQUIRED_COLUMNS,
      STATEMENT_TREE_COLUMNS.level3,
      STATEMENT_TREE_COLUMNS.code,
      ROW_ID_COLUMN,
    ].map((column) => [column, findColumn(headers, column)] as const),
  );
  const missingColumns = REQUIRED_COLUMNS.filter(
    (column) => columnIndexes.get(column) === -1,
  );
  if (missingColumns.length > 0) {
    return { status: "missing_columns", columns: [...missingColumns] };
  }

  const invalidLines: number[] = [];
  const seenRowIds = new Set<string>();
  const seenLabelPaths = new Set<string>();
  const rows = parsed.data.slice(1).flatMap((rawCells, index) => {
    const cells = sanitizeDataRow(rawCells, headers.length);
    const line = index + 2;
    const statementType = valueAt(
      cells,
      columnIndexes.get(STATEMENT_TREE_COLUMNS.statementType)!,
    )?.toUpperCase();
    const signText = valueAt(
      cells,
      columnIndexes.get(STATEMENT_TREE_COLUMNS.sign)!,
    );
    const level1 = valueAt(
      cells,
      columnIndexes.get(STATEMENT_TREE_COLUMNS.level1)!,
    );
    const level2 = valueAt(
      cells,
      columnIndexes.get(STATEMENT_TREE_COLUMNS.level2)!,
    );
    const level3 = valueAt(
      cells,
      columnIndexes.get(STATEMENT_TREE_COLUMNS.level3)!,
    );
    const code = valueAt(
      cells,
      columnIndexes.get(STATEMENT_TREE_COLUMNS.code)!,
    );
    const rowId = valueAt(cells, columnIndexes.get(ROW_ID_COLUMN)!);
    const sign: -1 | 1 | null =
      signText === "1" || signText === "+1" ? 1 : signText === "-1" ? -1 : null;

    // the backend rejects the whole save when two rows share a label path
    const labelPath = [statementType, level1, level2, level3 ?? ""].join("\0");
    if (
      (statementType !== "BS" && statementType !== "PL") ||
      sign === null ||
      level1 === null ||
      level2 === null ||
      (rowId !== null && seenRowIds.has(rowId)) ||
      seenLabelPaths.has(labelPath)
    ) {
      invalidLines.push(line);
      return [];
    }
    if (rowId !== null) seenRowIds.add(rowId);
    seenLabelPaths.add(labelPath);

    const row: DraftRow = {
      key: rowId ?? crypto.randomUUID(),
      row_id: rowId,
      statement_type: statementType,
      sign,
      level_1: level1,
      level_2: level2,
      level_3: level3,
      code,
    };
    return [row];
  });

  if (invalidLines.length > 0) {
    return { status: "invalid_rows", lines: invalidLines };
  }
  if (rows.length === 0) return { status: "empty" };
  return { status: "ok", rows };
}

function escapeSpreadsheetFormula(value: string | null | undefined): string {
  const text = value ?? "";

  const formulaText = text.trimStart();

  // OWASP's Excel-resistant mitigation puts a tab inside the quoted cell.
  // Import sanitization trims it, preserving literal apostrophes and row IDs.
  // Import already trims leading whitespace; keep the tab next to the sigil.
  // https://community.owasp.org/attacks/CSV_Injection
  return SPREADSHEET_FORMULA_PREFIX.test(formulaText)
    ? `\t${formulaText}`
    : text;
}

/** Serialize draft rows; callers validate completeness before downloading. */
export function buildStatementTreeCsv(rows: readonly DraftRow[]): string {
  return (
    CSV_TEMPLATE_BOM +
    Papa.unparse(
      {
        fields: [
          STATEMENT_TREE_COLUMNS.statementType,
          STATEMENT_TREE_COLUMNS.code,
          STATEMENT_TREE_COLUMNS.sign,
          STATEMENT_TREE_COLUMNS.level1,
          STATEMENT_TREE_COLUMNS.level2,
          STATEMENT_TREE_COLUMNS.level3,
          ROW_ID_COLUMN,
        ],
        data: rows.map((row) => [
          row.statement_type,
          escapeSpreadsheetFormula(row.code),
          row.sign,
          escapeSpreadsheetFormula(row.level_1),
          escapeSpreadsheetFormula(row.level_2),
          escapeSpreadsheetFormula(row.level_3),
          escapeSpreadsheetFormula(row.row_id),
        ]),
      },
      { delimiter: ";", quotes: true },
    )
  );
}
